import * as React from "react"
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip
} from "@mui/material"
import { ButtonSeeker, ModalSeeker, IconSeeker } from "@ui-seeker/core"

import { useMerge } from "../@hooks"


const ResultRow = ({ label, value, highlight = false }) => {
    if (value === 0 || value === undefined || value === null) return null

    return (
        <TableRow>
            <TableCell sx={{ fontWeight: 500, border: 'none', py: 0.75, pl: 2 }}>
                {label}
            </TableCell>
            <TableCell align="right" sx={{ border: 'none', py: 0.75 }}>
                <Chip
                    label={value}
                    size="small"
                    color={highlight ? "primary" : "default"}
                    variant={highlight ? "filled" : "outlined"}
                />
            </TableCell>
        </TableRow>
    )
}

const SectionHeader = ({ title }) => (
    <TableRow>
        <TableCell
            colSpan={2}
            sx={{
                bgcolor: 'grey.50',
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'text.secondary',
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}
        >
            {title}
        </TableCell>
    </TableRow>
)

const hasAnyValue = (result, keys) => {
    return keys.some(key => result[key] > 0)
}


export default function MergeResultModal() {
    const { mergeResult, handleCloseResultModal } = useMerge()

    if (!mergeResult) return null

    const isDryRun = mergeResult.dryRun

    // Group checks for conditional rendering
    const hasCoreData = hasAnyValue(mergeResult, [
        'updatedSkuMappings', 'updatedSkus', 'updatedLines',
        'updatedSkusToBeMapped', 'updatedRawToBeMapped', 'updatedPrimorFeed'
    ])
    const hasCustomerBrand = hasAnyValue(mergeResult, [
        'updatedCustomerBrands', 'insertedCustomerBrands'
    ])
    const hasRetailer = hasAnyValue(mergeResult, [
        'updatedRetailerBrand'
    ])
    const hasAlerts = hasAnyValue(mergeResult, [
        'updatedAlertPricing', 'updatedAlertStock',
        'updatedCustomReportStock', 'updatedCustomerSchedule'
    ])
    const hasConfig = hasAnyValue(mergeResult, [
        'updatedBrandMapping', 'updatedKeyword', 'updatedKeywordCategory',
        'updatedSection', 'updatedSectionCategory', 'updatedSites',
        'updatedBrandKeywords'
    ])
    const hasRelations = hasAnyValue(mergeResult, [
        'updatedBrandCategory', 'insertedBrandCategory',
        'updatedCustomerBrandScope', 'insertedCustomerBrandScope',
        'updatedCustomerCapabilities', 'insertedCustomerCapabilities',
        'updatedSovResourceBrands', 'insertedSovResourceBrands',
        'updatedVmsRaw', 'insertedVmsRaw'
    ])
    const hasQA = hasAnyValue(mergeResult, [
        'updatedQaIssues', 'updatedQaFilters', 'insertedQaFilters'
    ])

    return (
        <ModalSeeker
            isOpen={!!mergeResult}
            title={isDryRun ? "Dry Run Report" : "Merge Completed"}
            controller={handleCloseResultModal}
            hideActions
            containerProps={{ maxWidth: "sm" }}
        >
            <Box sx={{ p: 2 }}>
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 3,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: isDryRun ? "warning.lighter" : "success.lighter"
                }}>
                    <IconSeeker
                        icon={isDryRun ? "flask" : "circle-check"}
                        sx={{
                            fontSize: 24,
                            color: isDryRun ? "warning.main" : "success.main"
                        }}
                    />
                    <Typography variant="body1" fontWeight="medium">
                        {isDryRun
                            ? "Test completed - No changes were made"
                            : "Merge executed successfully"
                        }
                    </Typography>
                </Box>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {isDryRun ? "Records that would be affected:" : "Records affected:"}
                </Typography>

                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ mb: 2, maxHeight: 400, overflow: 'auto' }}
                >
                    <Table size="small" stickyHeader>
                        <TableBody>
                            {/* Main Brand Info */}
                            <ResultRow
                                label="Brands merged"
                                value={mergeResult.mergedBrands}
                                highlight
                            />

                            {/* Core Data Tables */}
                            {hasCoreData && (
                                <>
                                    <SectionHeader title="Core Data" />
                                    <ResultRow
                                        label="SKU Mappings"
                                        value={mergeResult.updatedSkuMappings}
                                    />
                                    <ResultRow
                                        label="SKUs"
                                        value={mergeResult.updatedSkus}
                                    />
                                    <ResultRow
                                        label="Lines"
                                        value={mergeResult.updatedLines}
                                    />
                                    <ResultRow
                                        label="Pending SKU tasks"
                                        value={mergeResult.updatedSkusToBeMapped}
                                    />
                                    <ResultRow
                                        label="Pending Raw tasks"
                                        value={mergeResult.updatedRawToBeMapped}
                                    />
                                    <ResultRow
                                        label="Primor Feed"
                                        value={mergeResult.updatedPrimorFeed}
                                    />
                                </>
                            )}

                            {/* Customer-Brand Relations */}
                            {hasCustomerBrand && (
                                <>
                                    <SectionHeader title="Customer-Brand" />
                                    <ResultRow
                                        label="Relations deleted"
                                        value={mergeResult.updatedCustomerBrands}
                                    />
                                    <ResultRow
                                        label="Relations added"
                                        value={mergeResult.insertedCustomerBrands}
                                    />
                                </>
                            )}

                            {/* Retailer world */}
                            {hasRetailer && (
                                <>
                                    <SectionHeader title="Retailer" />
                                    <ResultRow
                                        label="Retailer Brands"
                                        value={mergeResult.updatedRetailerBrand}
                                    />
                                </>
                            )}

                            {/* Alerts & Schedules (JSON filters) */}
                            {hasAlerts && (
                                <>
                                    <SectionHeader title="Alerts & Schedules" />
                                    <ResultRow
                                        label="Pricing Alerts"
                                        value={mergeResult.updatedAlertPricing}
                                    />
                                    <ResultRow
                                        label="Stock Alerts"
                                        value={mergeResult.updatedAlertStock}
                                    />
                                    <ResultRow
                                        label="Custom Reports"
                                        value={mergeResult.updatedCustomReportStock}
                                    />
                                    <ResultRow
                                        label="Schedules"
                                        value={mergeResult.updatedCustomerSchedule}
                                    />
                                </>
                            )}

                            {/* Configuration Tables */}
                            {hasConfig && (
                                <>
                                    <SectionHeader title="Configuration" />
                                    <ResultRow
                                        label="Brand Mappings"
                                        value={mergeResult.updatedBrandMapping}
                                    />
                                    <ResultRow
                                        label="Keywords"
                                        value={mergeResult.updatedKeyword}
                                    />
                                    <ResultRow
                                        label="Keyword Categories"
                                        value={mergeResult.updatedKeywordCategory}
                                    />
                                    <ResultRow
                                        label="Sections"
                                        value={mergeResult.updatedSection}
                                    />
                                    <ResultRow
                                        label="Section Categories"
                                        value={mergeResult.updatedSectionCategory}
                                    />
                                    <ResultRow
                                        label="Sites"
                                        value={mergeResult.updatedSites}
                                    />
                                    <ResultRow
                                        label="Brand Keywords deleted"
                                        value={mergeResult.updatedBrandKeywords}
                                    />
                                </>
                            )}

                            {/* Relational Tables */}
                            {hasRelations && (
                                <>
                                    <SectionHeader title="Relations" />
                                    <ResultRow
                                        label="Brand-Category updated"
                                        value={mergeResult.updatedBrandCategory}
                                    />
                                    <ResultRow
                                        label="Brand-Category added"
                                        value={mergeResult.insertedBrandCategory}
                                    />
                                    <ResultRow
                                        label="Customer Brand Scope updated"
                                        value={mergeResult.updatedCustomerBrandScope}
                                    />
                                    <ResultRow
                                        label="Customer Brand Scope added"
                                        value={mergeResult.insertedCustomerBrandScope}
                                    />
                                    <ResultRow
                                        label="Customer Capabilities updated"
                                        value={mergeResult.updatedCustomerCapabilities}
                                    />
                                    <ResultRow
                                        label="Customer Capabilities added"
                                        value={mergeResult.insertedCustomerCapabilities}
                                    />
                                    <ResultRow
                                        label="SOV Resource Brands updated"
                                        value={mergeResult.updatedSovResourceBrands}
                                    />
                                    <ResultRow
                                        label="SOV Resource Brands added"
                                        value={mergeResult.insertedSovResourceBrands}
                                    />
                                    <ResultRow
                                        label="VMS Raw updated"
                                        value={mergeResult.updatedVmsRaw}
                                    />
                                    <ResultRow
                                        label="VMS Raw added"
                                        value={mergeResult.insertedVmsRaw}
                                    />
                                </>
                            )}

                            {/* QA Tables */}
                            {hasQA && (
                                <>
                                    <SectionHeader title="QA" />
                                    <ResultRow
                                        label="QA Issues"
                                        value={mergeResult.updatedQaIssues}
                                    />
                                    <ResultRow
                                        label="QA Filters updated"
                                        value={mergeResult.updatedQaFilters}
                                    />
                                    <ResultRow
                                        label="QA Filters added"
                                        value={mergeResult.insertedQaFilters}
                                    />
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <ButtonSeeker
                        variant="contained"
                        color={isDryRun ? "warning" : "primary"}
                        onClick={handleCloseResultModal}
                    >
                        Close
                    </ButtonSeeker>
                </Box>
            </Box>
        </ModalSeeker>
    )
}
